// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------
import React, { useState } from 'react';
import clsx from 'clsx';
import { ComboBox, IComboBox, IComboBoxOption, IDropdownOption, Spinner, SpinnerSize, TextField } from 'office-ui-fabric-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { actionStatusSelector, LoadingState } from './../../store/actionStatus';
import { getWicAddressData, GET_WIC_ADDRESS_DATA } from './../../store/address';
import { useCurrentCountryAndProvinces } from './../../store/provinces';
import Styles from './walkInCenterStyles.module.scss';
import { sharedComboBoxOptionStyles } from 'src/helpers/sharedComboBoxOptionStyles';

export interface ProvinceType {
    ISOCode: string | number;
    ISOName: string;
}

export default function WalkInCenterSearch(props: any) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [selectedOffer, setSelectedOffer] = useState<any>({});
    const [city, setCity] = useState<string | undefined>();

    const wicAddressState = useSelector(state => actionStatusSelector(state)(GET_WIC_ADDRESS_DATA));

    const { countryCode, provinces } = useCurrentCountryAndProvinces();

    const provinceDropdownOptions = (provinces || [] as ProvinceType[])
        .sort()
        .map((option: {
            ISOCode: string | number;
            ISOName: string;
        }) => ({ key: option.ISOCode, text: option.ISOName } as IDropdownOption));

    function onCityInputChanged(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) {
        setCity(newValue);
    }

    function onProvinceChange(event: React.FormEvent<IComboBox>, option?: IComboBoxOption, index?: number, value?: string) {
        if (option) {
            setSelectedOffer(option);
        }
    }

    function onSearchClick() {
        if (selectedOffer && city) {
            dispatch(getWicAddressData({ city, province: selectedOffer.key, country: countryCode }));
        }
    }

    return (<section className={clsx(Styles.searchContainer)}>
        <div className="ms-Grid">
            <div className={clsx(Styles.headerContainer, 'ms-Grid-row')}>
                <div className={clsx('ms-Grid-col', 'ms-sm4')}>
                    <ComboBox autoComplete="on"
                        id="state_province"
                        label={t('state_province')}
                        ariaLabel={t('state_province')}
                        errorMessage={undefined}
                        selectedKey={selectedOffer.key}
                        onChange={onProvinceChange}
                        options={provinceDropdownOptions}
                        comboBoxOptionStyles={sharedComboBoxOptionStyles}
                        required={true} />
                </div>
                <div className={clsx('ms-Grid-col', 'ms-sm4')}>
                    <TextField
                        name="city"
                        id="city"
                        required={true}
                        label={t('city')}
                        onChange={onCityInputChanged}
                        value={city}
                        ariaLabel={t('city')}
                        maxLength={35} />
                </div>
                <div className={clsx('ms-Grid-col', 'ms-sm4')}>
                    <button
                        id="btn_search"
                        className={clsx(Styles.buttonPrimary, Styles.button, Styles.buttonAlign)}
                        onClick={onSearchClick} disabled={wicAddressState.status === LoadingState.started || city === undefined || city.length === 0 || Object.keys(selectedOffer).length === 0}>
                        {t('search')}

                    </button>
                    {(wicAddressState.status === LoadingState.started) && <Spinner id="search-loading" className={Styles.spinnerStyle} labelPosition="top" size={SpinnerSize.medium} />}
                </div>
            </div>
        </div>
    </section>);
}